import { type ReactNode, useEffect, useRef, useState } from 'react';
import { AccessibilityInfo, Animated, Easing, Platform } from 'react-native';
import { useReducedMotion } from 'react-native-reanimated';

export function AdvisorEntrance({
  children,
  enabled = true,
  duration = 380,
  className,
  testID,
}: {
  children: ReactNode;
  enabled?: boolean;
  duration?: number;
  className?: string;
  testID?: string;
}) {
  const initialReducedMotion = useReducedMotion();
  const [reducedMotion, setReducedMotion] = useState(initialReducedMotion);
  const active = enabled && !reducedMotion;
  const progress = useRef(new Animated.Value(active ? 0 : 1)).current;

  useEffect(() => {
    const subscription = AccessibilityInfo.addEventListener(
      'reduceMotionChanged',
      setReducedMotion,
    );
    return () => subscription.remove();
  }, []);

  useEffect(() => {
    progress.setValue(active ? 0 : 1);
    if (!active) return;

    const animation = Animated.timing(progress, {
      toValue: 1,
      duration,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: Platform.OS !== 'web',
      isInteraction: false,
    });
    animation.start();
    return () => animation.stop();
  }, [active, duration, progress]);

  return (
    <Animated.View
      className={className}
      testID={testID}
      style={{
        opacity: active ? progress : 1,
        transform: [
          {
            translateY: active
              ? progress.interpolate({ inputRange: [0, 1], outputRange: [6, 0] })
              : 0,
          },
        ],
      }}
    >
      {children}
    </Animated.View>
  );
}
